import { FULL_HD, HD, RENDERING_CONTEXT } from '@bedrock-rdk/constants';
import _memoize from 'lodash/memoize';
import { PRIMARY, TERTIARY, makeThemeColor } from '@tornado-rdk/theme';
import { FONT, getFont } from '@tornado-rdk/typography';
import { TRANSPARENT } from '../utils/colors';
import { FOCUSED_Z_INDEX, UNFOCUSED_Z_INDEX, cacheResolver, makeDefaultStylingContext } from '../utils/style';
import { sizes, templates } from './banner.constants';
import {
  BORDER_COLOR,
  BORDER_RADIUS,
  BORDER_WIDTH,
  INNER_BORDER_COLOR,
  FOCUSED_BORDER_ALPHA,
  UNFOCUSED_BORDER_ALPHA,
} from '../utils/focus';

export const defaultContext = makeDefaultStylingContext();

export const SCALE_FOCUSED = 1.02;

export const PADDING = 24;

export const LOGO_MARGIN_BOTTOM = 8;

export const ICON_HEIGHT = {
  [HD]: 24,
  [FULL_HD]: 32,
};

export const LOCK_ICON_HEIGHT = {
  [HD]: 32,
  [FULL_HD]: 40,
};

export const colors = {
  border: makeThemeColor(PRIMARY),
  overlay: makeThemeColor(TERTIARY),
};

export const getAdaptiveSizes = (width, height) => ({
  w: width,
  h: height,
  Border: {
    w: width + BORDER_WIDTH[defaultContext.screenSize] * 2,
    h: height + BORDER_WIDTH[defaultContext.screenSize] * 2,
  },
  Image: {
    w: width,
    h: height,
  },
  Content: {
    w: width - PADDING * 2,
    h: height - PADDING * 2,
  },
});

export const getInitialStyle = ({ screenSize, colorMode } = defaultContext) => {
  const { w, h } = sizes[templates.Banner][RENDERING_CONTEXT.GENERAL][screenSize];
  const defaultSizes = getAdaptiveSizes(w, h);
  const borderSize = BORDER_WIDTH[screenSize];

  return {
    ...defaultSizes,
    ...{
      zIndex: UNFOCUSED_Z_INDEX,
      Border: {
        alpha: 0,
        x: -borderSize,
        y: -borderSize,
        color: TRANSPARENT,
        shader: {
          radius: BORDER_RADIUS[screenSize],
          outline: BORDER_WIDTH[screenSize],
          strokeColor: TRANSPARENT,
          innerColor: TRANSPARENT,
        },
      },
      Image: {
        shader: {
          radius: BORDER_RADIUS[screenSize],
          overlayColor: colors.overlay[colorMode],
        },
      },
      Content: {
        flex: { direction: 'column-reverse' },
        x: PADDING,
        y: PADDING,
        Description: {
          text: {
            ...getFont(FONT.OTTAWA_1),
            maxLines: 3,
            textOverflow: '...',
          },
        },
        Title: {
          text: {
            ...getFont(FONT.KINSHASA_3),
            maxLines: 2,
            textOverflow: '...',
          },
        },
        Logo: {
          flexItem: {
            marginBottom: LOGO_MARGIN_BOTTOM,
          },
        },
      },
    },
  };
};

export const getFocusedStyle = _memoize(
  ({ colorMode }) => ({
    zIndex: FOCUSED_Z_INDEX,
    Border: {
      smooth: {
        alpha: FOCUSED_BORDER_ALPHA,
        scale: SCALE_FOCUSED,
        'shader.strokeColor': BORDER_COLOR[colorMode],
        'shader.innerColor': INNER_BORDER_COLOR[colorMode],
        'shader.scale': SCALE_FOCUSED,
      },
    },
    Image: {
      smooth: {
        scale: SCALE_FOCUSED,
        'shader.scale': SCALE_FOCUSED,
      },
    },
  }),
  cacheResolver,
);

export const unFocusedStyle = {
  zIndex: UNFOCUSED_Z_INDEX,
  Border: {
    smooth: {
      alpha: UNFOCUSED_BORDER_ALPHA,
      scale: 1,
      'shader.strokeColor': TRANSPARENT,
      'shader.innerColor': TRANSPARENT,
      'shader.scale': 1.0,
    },
  },
  Image: {
    smooth: {
      scale: 1,
      'shader.scale': 1.0,
    },
  },
};
